type FormattingType = "number" | "percentage" | "currency" | "marker" | "date";

interface NumberFormatOptions {
  style?: "decimal" | "currency" | "percent";
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  currencyDisplay?: "symbol" | "narrowSymbol" | "code" | "name";
  locale?: string;
}

interface MarkerFormatOptions {
  [key: string]: string; // Maps marker values to colors
}

interface DateFormatOptions {
  style?: "short" | "medium" | "long" | "full";
  dateStyle?: "short" | "medium" | "long" | "full";
  timeStyle?: "short" | "medium" | "long" | "full";
  locale?: string;
}

/**
 * Helper function to format date values in a human-readable format
 * @param value The date value to format (can be ISO string or Date object)
 * @param options Optional formatting options
 * @returns Formatted date string
 */
const formatDateValue = (value: string | number | Date, options?: DateFormatOptions): string => {
  if (!value) return "-";

  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return String(value); // Return original if not a valid date
    }

    const locale = options?.locale || "en-GB";

    // If specific date/time styles are provided, use them
    if (options?.dateStyle || options?.timeStyle) {
      return new Intl.DateTimeFormat(locale, {
        dateStyle: options.dateStyle,
        timeStyle: options.timeStyle,
      }).format(date);
    }

    // Default format: "15 Jan 2021" (no time for dates)
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return String(value); // Return original if formatting fails
  }
};

/**
 * Formats a date string for use in filenames (YYYY-MM-DD format)
 * @param dateStr The date string (ISO format) or null/undefined
 * @returns Formatted date string in YYYY-MM-DD format
 * @example
 * formatDateForFilename('2024-01-31T00:00:00Z') // '2024-01-31'
 * formatDateForFilename(null) // Today's date in YYYY-MM-DD format
 */
export const formatDateForFilename = (dateStr?: string | null): string => {
  if (!dateStr || dateStr === "latest") {
    return new Date().toISOString().split("T")[0];
  }
  // Extract YYYY-MM-DD from ISO string
  return dateStr.split("T")[0];
};

/**
 * Generates a PDF filename for report exports
 * Uses report.type if available, otherwise falls back to report.name
 * Date format is YYYYMMDD, and colons are removed from resource names
 * @param report The report object with optional type and name
 * @param asAtDate The as-at date string (ISO format)
 * @param resourceName Optional resource name to include in filename
 * @returns Formatted PDF filename
 * @example
 * generatePDFFilename({ type: 'Quarterly Report', name: 'Q1 2024' }, '2024-01-31', 'Portfolio: ABC')
 * // Returns: 'Quarterly Report - 20240131 - Portfolio ABC.pdf'
 */
export const generatePDFFilename = (
  report: { name?: string; type?: string },
  asAtDate: string,
  resourceName?: string | null
): string => {
  const reportLabel = report.type || report.name || "Report";
  const dateForFilename = (asAtDate || new Date().toISOString())
    .split("T")[0]
    .replace(/-/g, ""); // YYYYMMDD format
  const sanitizedResourceName = resourceName?.replace(/:/g, "") || null;

  return sanitizedResourceName
    ? `${reportLabel} - ${dateForFilename} - ${sanitizedResourceName}.pdf`
    : `${reportLabel} - ${dateForFilename}.pdf`;
};

/**
 * Context object for PDF filename template variables
 */
export interface FilenameTemplateContext {
  /** Report name */
  report_name?: string;
  /** Report type (if different from name) */
  report_type?: string;
  /** As-at date in ISO format (YYYY-MM-DD) */
  date?: string;
  /** Resource/portfolio name */
  resource_name?: string;
  /** Index number for batch exports (1-based) */
  index?: number;
  /** Total count for batch exports */
  total?: number;
}

/**
 * Available template tags for PDF filename customization
 */
export const FILENAME_TEMPLATE_TAGS = [
  { tag: "{report_name}", description: "Report name", example: "Quarterly Report" },
  { tag: "{report_type}", description: "Report type (falls back to name)", example: "Factsheet" },
  { tag: "{date}", description: "Date in YYYYMMDD format", example: "20240131" },
  { tag: "{date:YYYY-MM-DD}", description: "Date in YYYY-MM-DD format", example: "2024-01-31" },
  { tag: "{date:DD/MM/YYYY}", description: "Date in DD/MM/YYYY format", example: "31/01/2024" },
  { tag: "{resource_name}", description: "Resource/portfolio name", example: "Client Portfolio" },
  { tag: "{index}", description: "Batch index number", example: "1" },
  { tag: "{total}", description: "Total batch count", example: "10" },
] as const;

/**
 * Formats a date string according to the specified format
 * @param dateStr ISO date string (YYYY-MM-DD)
 * @param format Target format
 * @returns Formatted date string
 */
const formatDateWithPattern = (dateStr: string, format: string): string => {
  const date = dateStr.split("T")[0]; // Ensure we have just the date part
  const [year, month, day] = date.split("-");
  
  switch (format) {
    case "YYYYMMDD":
      return `${year}${month}${day}`;
    case "YYYY-MM-DD":
      return date;
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`;
    case "DD-MM-YYYY":
      return `${day}-${month}-${year}`;
    default:
      return `${year}${month}${day}`; // Default to YYYYMMDD
  }
};

/**
 * Generates a default filename template based on report and context
 * This matches the current generatePDFFilename behavior
 * @param context Template context
 * @returns Default template string
 */
export const getDefaultFilenameTemplate = (
  context: FilenameTemplateContext
): string => {
  const hasResource = context.resource_name && context.resource_name.trim() !== "";
  return hasResource
    ? "{report_type} - {date} - {resource_name}"
    : "{report_type} - {date}";
};

/**
 * Applies template variables to a filename template string
 * @param template Template string with {variable} placeholders
 * @param context Context object with variable values
 * @returns Resolved filename (without .pdf extension)
 * @example
 * applyFilenameTemplate("{report_name} - {date} - {resource_name}", {
 *   report_name: "Quarterly Report",
 *   date: "2024-01-31",
 *   resource_name: "Portfolio ABC"
 * })
 * // Returns: "Quarterly Report - 20240131 - Portfolio ABC"
 */
export const applyFilenameTemplate = (
  template: string,
  context: FilenameTemplateContext
): string => {
  const date = context.date || new Date().toISOString().split("T")[0];
  const reportType = context.report_type || context.report_name || "Report";
  const reportName = context.report_name || "Report";
  const resourceName = context.resource_name?.replace(/:/g, "") || "";
  
  let result = template
    // Handle date with format specifier
    .replace(/\{date:([^}]+)\}/g, (_, format) => formatDateWithPattern(date, format))
    // Handle plain date (defaults to YYYYMMDD)
    .replace(/\{date\}/g, formatDateWithPattern(date, "YYYYMMDD"))
    // Handle other variables
    .replace(/\{report_type\}/g, reportType)
    .replace(/\{report_name\}/g, reportName)
    .replace(/\{resource_name\}/g, resourceName)
    .replace(/\{index\}/g, context.index?.toString() || "")
    .replace(/\{total\}/g, context.total?.toString() || "");
  
  // Clean up any double spaces or trailing/leading separators
  result = result
    .replace(/\s+-\s+-/g, " -") // Remove empty segments like " - - "
    .replace(/\s+-\s*$/g, "") // Remove trailing " -" or " - "
    .replace(/^\s*-\s+/g, "") // Remove leading "- "
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .trim();
  
  return result;
};

/**
 * One row of batch metadata used to resolve filename templates the same way as
 * the reports batch run flow when generating PDFs.
 */
export interface BatchFilenameTemplateRow {
  /** Display name of the report template for this batch row */
  report_name: string;
  /** Primary resource display name, or `"Batch"` when multiple resources are selected */
  resource_name: string;
}

/**
 * Resolves the PDF batch `id` string for each row (filename stem, no `.pdf` suffix).
 * Matches the context fields used when building `PDFGenerationParams.id` for batch runs.
 *
 * @param template Filename template with `{tag}` placeholders
 * @param dateIso As-at date in `YYYY-MM-DD` form
 * @param rows One entry per PDF in the batch
 * @returns Resolved stems in row order
 */
export const resolveBatchFilenameStems = (
  template: string,
  dateIso: string,
  rows: readonly BatchFilenameTemplateRow[]
): string[] => {
  const total = rows.length;
  return rows.map((row, index) =>
    applyFilenameTemplate(template, {
      report_name: row.report_name,
      report_type: row.report_name,
      date: dateIso,
      resource_name: row.resource_name,
      index: index + 1,
      total,
    })
  );
};

/**
 * Duplicate filename stem with the 1-based row numbers that would collide.
 */
export interface BatchFilenameDuplicateGroup {
  /** Resolved filename stem (used as batch PDF `id`) */
  stem: string;
  /** 1-based indices into the batch */
  rowNumbers: number[];
}

/**
 * Result of {@link validateBatchFilenameTemplate}.
 */
export interface BatchFilenameTemplateValidation {
  /** True when every row has a non-empty unique stem */
  ok: boolean;
  /** Non-empty stems that would be reused */
  duplicateGroups?: BatchFilenameDuplicateGroup[];
  /** 1-based row numbers where the stem is empty or whitespace-only */
  emptyStemRows?: number[];
  /**
   * True when a resolved stem still contains `{`, usually from an incomplete
   * placeholder (e.g. `{index` instead of `{index}`).
   */
  hasLiteralBraceInStems?: boolean;
}

/**
 * Validates that a filename template yields a unique, non-empty stem for every batch row.
 * Used before async batch PDF generation (each PDF `id` must be unique).
 *
 * @param template Filename template string
 * @param dateIso As-at date `YYYY-MM-DD`
 * @param rows Batch rows in generation order
 */
export const validateBatchFilenameTemplate = (
  template: string,
  dateIso: string,
  rows: readonly BatchFilenameTemplateRow[]
): BatchFilenameTemplateValidation => {
  if (rows.length === 0) {
    return { ok: true };
  }

  const stems = resolveBatchFilenameStems(template, dateIso, rows);
  const hasLiteralBraceInStems = stems.some((s) => s.includes("{"));
  const emptyStemRows: number[] = [];
  stems.forEach((stem, i) => {
    if (!stem.trim()) {
      emptyStemRows.push(i + 1);
    }
  });

  const stemToRows = new Map<string, number[]>();
  for (let i = 0; i < stems.length; i++) {
    const stem = stems[i];
    if (!stem.trim()) {
      continue;
    }
    const rowNumbers = stemToRows.get(stem) ?? [];
    rowNumbers.push(i + 1);
    stemToRows.set(stem, rowNumbers);
  }

  const duplicateGroups: BatchFilenameDuplicateGroup[] = [];
  for (const [stem, rowNumbers] of stemToRows) {
    if (rowNumbers.length > 1) {
      duplicateGroups.push({ stem, rowNumbers });
    }
  }
  duplicateGroups.sort((a, b) => a.rowNumbers[0] - b.rowNumbers[0]);

  if (
    duplicateGroups.length > 0 ||
    emptyStemRows.length > 0 ||
    hasLiteralBraceInStems
  ) {
    return {
      ok: false,
      duplicateGroups: duplicateGroups.length > 0 ? duplicateGroups : undefined,
      emptyStemRows: emptyStemRows.length > 0 ? emptyStemRows : undefined,
      hasLiteralBraceInStems: hasLiteralBraceInStems ? true : undefined,
    };
  }
  return { ok: true };
};

const formatRowNumbersForMessage = (rowNumbers: number[]): string => {
  if (rowNumbers.length === 2) {
    return `${rowNumbers[0]} and ${rowNumbers[1]}`;
  }
  return rowNumbers.join(", ");
};

/**
 * Human-readable explanation for failed batch filename validation (toast or inline alert).
 *
 * @param validation Result from {@link validateBatchFilenameTemplate}
 * @returns Empty string when `validation.ok`
 */
export const describeBatchFilenameTemplateValidation = (
  validation: BatchFilenameTemplateValidation
): string => {
  if (validation.ok) {
    return "";
  }
  const lines: string[] = [];

  if (validation.hasLiteralBraceInStems) {
    lines.push(
      "A `{` is still showing in the resolved name. Placeholders need both braces (for example `{index}`), or pick a tag from the help list."
    );
  }

  if (validation.duplicateGroups?.length) {
    lines.push(
      "Multiple rows would produce the same PDF filename. Add something that varies per row, such as `{report_name}` or `{index}`."
    );
    for (const g of validation.duplicateGroups.slice(0, 3)) {
      lines.push(
        `• Rows ${formatRowNumbersForMessage(g.rowNumbers)}: ${g.stem}.pdf`
      );
    }
    const remaining = validation.duplicateGroups.length - 3;
    if (remaining > 0) {
      lines.push(`${remaining} more duplicate name${remaining === 1 ? "" : "s"} not listed.`);
    }
  }

  if (validation.emptyStemRows?.length) {
    lines.push(
      `Some rows would have an empty filename (rows ${validation.emptyStemRows.join(", ")}).`
    );
  }

  return lines.join("\n");
};

/**
 * Generates a preview of what the filename will look like with sample data
 * @param template Template string
 * @param context Partial context (missing values will use placeholders)
 * @returns Preview string showing the resolved template
 */
export const previewFilenameTemplate = (
  template: string,
  context: Partial<FilenameTemplateContext>
): string => {
  const fullContext: FilenameTemplateContext = {
    report_name: context.report_name || "Report Name",
    report_type: context.report_type || context.report_name || "Report Type",
    date: context.date || new Date().toISOString().split("T")[0],
    resource_name: context.resource_name || "Resource Name",
    index: context.index || 1,
    total: context.total || 10,
  };
  return applyFilenameTemplate(template, fullContext) + ".pdf";
};

/**
 * Formats a value based on the provided formatting configuration from the API
 * @param value The value to format
 * @param column The column configuration from the API
 * @returns The formatted value as a string
 */
export const formatColumnValue = (
  value: string | number | Date | null | undefined,
  column: {
    id: string;
    data_type?: string;
    formatting?: {
      type?: FormattingType;
      options?: NumberFormatOptions | MarkerFormatOptions | DateFormatOptions;
      decimal_places?: number;
      decimals?: number;
      prefix?: string;
      suffix?: string;
    };
  }
): string => {
  // If value is null/undefined, return a dash
  if (value == null) {
    return "-";
  }

  // Get the formatting configuration
  const formatting = column.formatting;

  // Handle date formatting first, even without explicit formatting config
  if (column.data_type === "date" && !formatting?.type) {
    return formatDateValue(value);
  }

  if (!formatting) {
    return String(value);
  }

  // Handle different types of formatting
  const type =
    formatting.type ||
    (column.data_type === "percentage"
      ? "percentage"
      : column.data_type === "date"
        ? "date"
        : "number");

  switch (type) {
    case "number": {
      const options = (formatting.options as NumberFormatOptions) || {};
      const decimals =
        formatting.decimals ??
        formatting.decimal_places ??
        options.maximumFractionDigits ??
        2;
      const prefix = formatting.prefix || "";
      const suffix = formatting.suffix || "";

      // If the style is currency, use currency formatting
      if (options.style === "currency") {
        return new Intl.NumberFormat(options.locale || "en-GB", {
          style: "currency",
          currency: options.currency || "GBP",
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
          currencyDisplay: options.currencyDisplay || "narrowSymbol",
        }).format(value);
      }

      // Otherwise use decimal formatting
      return `${prefix}${new Intl.NumberFormat(options.locale || "en-GB", {
        style: "decimal",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value)}${suffix}`;
    }

    case "percentage": {
      const options = (formatting.options as NumberFormatOptions) || {};
      const decimals =
        formatting.decimals ??
        formatting.decimal_places ??
        options.maximumFractionDigits ??
        2;
      const prefix = formatting.prefix || "";
      const suffix = formatting.suffix || "";
      // Convert the value to a decimal if it's already in percentage form (e.g., 50 -> 0.5)
      const decimalValue = value;

      return `${prefix}${new Intl.NumberFormat(options.locale || "en-GB", {
        style: "percent",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(decimalValue)}${suffix}`;
    }

    case "currency": {
      const options = (formatting.options as NumberFormatOptions) || {};
      const decimals =
        formatting.decimals ??
        formatting.decimal_places ??
        options.maximumFractionDigits ??
        2;

      return new Intl.NumberFormat(options.locale || "en-GB", {
        style: "currency",
        currency: options.currency || "GBP",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        currencyDisplay: options.currencyDisplay || "narrowSymbol",
      }).format(value);
    }

    case "marker": {
      if (formatting.options && typeof value === "string") {
        const markerOptions = formatting.options as MarkerFormatOptions;
        const markerColor = markerOptions[value];
        if (markerColor) {
          // Return a special format that can be interpreted by the rendering component
          return JSON.stringify({
            type: "marker",
            value: value,
            color: markerColor,
          });
        }
      }
      return String(value);
    }

    case "date": {
      const options = (formatting.options as DateFormatOptions) || {};
      const prefix = formatting.prefix || "";
      const suffix = formatting.suffix || "";

      return `${prefix}${formatDateValue(value, options)}${suffix}`;
    }

    default: {
      const prefix = formatting.prefix || "";
      const suffix = formatting.suffix || "";
      return `${prefix}${String(value)}${suffix}`;
    }
  }
};
